import React, { useEffect, useMemo, useState } from 'react';
import CommonModal from '../../Components/CommonModal';
import CommonTable from '../../Components/CommonTable';
import ButtonLoader from '../../Components/ButtonLoader';
import AddOnCard from '../../Dashboard/AddOns/AddOnCard';
import StatusBadge from '../../Components/StatusBadge';
import { getOrganizationAssessmentSettingsService, updateOrganizationAssessmentSettingsService } from '../../services';
import { buildGradeBuckets } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

// ─────────────────────────────────────────────────────────────
// ScanCadenceModal — the org's reassessment cadence, one monthly frequency
// per impact tier.
//
// This was the top half of the Scan Schedule page (/tpcrm/schedule). That page
// is gone: its other half, the "Per-Vendor Scan Log", was 5 of 7 columns
// identical to the Vendors table off the same `third_party_monitored_vendors`
// call, and its only two unique columns (Expires, Current Assessment Grade)
// already appear in VendorProfileModal's Assessment tab.
//
// Being a modal is also cheaper than the page it replaced: the settings request
// fires when someone opens it, not on every visit to a route in the sidebar.
//
// ── What `monthly_frequency` actually means ──────────────────────────
// MONTHS BETWEEN SCANS, not scans per month — despite the field name, and
// despite the old UI labelling it "Monthly Frequency" / "times per month".
// Nothing in the frontend computes a date from it (it is used server-side
// only), so this is inferred from the data, but the evidence is one-sided:
//
//   • Stored values are 12 / 12 / 12 / 6 / 3 — annual, semi-annual and
//     quarterly, the standard TPRM reassessment intervals.
//   • Observed vendors: Last Scan 17 Aug 2026 -> Next Scan 17 Nov 2026 is
//     exactly 3 months; 29 Jul 2026 -> 6 Aug 2027 is ~12. Both match their
//     tier's value as MONTHS.
//   • Read as scans-per-month, 12 would mean re-sending a vendor security
//     questionnaire every 2.5 days, and the config would be inverted
//     nonsense (safest vendors scanned 4x more often than the riskiest).
//     Read as months, it is textbook: annual for low risk, quarterly for high.
//
// Worth confirming with the backend, since the field name says the opposite.
//
// ── Why this is a ladder and not five cards ──────────────────────────
// The five tiers are not independent settings, they are one ORDERED scale:
// risk rises from (A) Very Unlikely to (F) Very Likely, so the interval should
// SHRINK as it does. Five equal cards with a 50px colour slab each hid that —
// the colour was the loudest thing on screen while carrying no information
// beyond "this is tier 3".
//
// The tier letters are A/B/C/D/F — exactly GRADE_ORDER, skipping E like it
// does. These tiers ARE the vendor's security grade: graded A means very
// unlikely to be a problem, graded F means very likely. That lets the modal
// show how many vendors each row actually governs, counted from the list the
// Vendors page has already fetched — no extra request. Setting a cadence
// without knowing it covers 7 vendors or 0 was the real gap here.
//
// So: one row per tier in risk order, how many vendors sit in it, the interval
// in plain words, a portfolio summary above, and warnings for the two states
// that need action — a tier left longer than a safer one, and a tier holding
// vendors with no cadence set at all.
//
// An earlier pass also drew a proportional bar per row. Removed: a filled
// track reads as PROGRESS, and nothing here progresses. It was also normalised
// to the fastest tier, which made it actively misleading — five tiers all at
// 12 months rendered as five FULL bars, i.e. "maximum scanning everywhere"
// when it meant annual-only. The interval text and the inversion warning
// already do the work it was supposed to.
// ─────────────────────────────────────────────────────────────

// impact key 1-5 → months between scans. Ordered by rising risk; `short` is
// the letter the rest of the portal grades with.
const IMPACT_TIERS = [
    { impact: 1, short: 'A', label: 'Very Unlikely', color: '#19A07E' },
    { impact: 2, short: 'B', label: 'Unlikely', color: '#D9BF21' },
    { impact: 3, short: 'C', label: 'Possible', color: '#DE9E30' },
    { impact: 4, short: 'D', label: 'Likely', color: '#E56A49' },
    { impact: 5, short: 'F', label: 'Very Likely', color: '#D24646' },
];

// Say the interval the way a person would.
const intervalLabel = (months) => {
    if (!months) return 'not scheduled';
    if (months === 1) return 'every month';
    if (months === 3) return 'quarterly';
    if (months === 6) return 'twice a year';
    if (months === 12) return 'once a year';
    if (months % 12 === 0) {
        const years = months / 12;
        return `every ${years} years`;
    }
    return `every ${months} months`;
};

function ScanCadenceModal({ show, onHide, onSaved, vendors = [] }) {
    const [frequencyByImpact, setFrequencyByImpact] = useState({});
    const [savingImpact, setSavingImpact] = useState(null);
    const [loading, setLoading] = useState(false);
    // Inline rather than a toast: `.custom-toast` is fixed-position and would
    // sit behind the modal backdrop, so the one message that matters here
    // would be the one you cannot see.
    const [message, setMessage] = useState(null);
    // Cadence decides `next_assessment_scheduled_date`, which is the Vendors
    // table's Next Scan column — the table sitting right behind this modal. So
    // the caller has to be told to refetch, or those dates stay stale until
    // something else happens to trigger a reload. Flagged rather than fired per
    // save: five tiers edited in one sitting should cost one refetch, not five.
    const [savedAny, setSavedAny] = useState(false);

    // Fetched on open, not on mount, so a closed modal costs nothing.
    useEffect(() => {
        if (!show) return undefined;
        let cancelled = false;

        const fetchSettings = async () => {
            setLoading(true);
            setMessage(null);
            setSavedAny(false);
            try {
                const response = await getOrganizationAssessmentSettingsService();
                const settings = response?.statusCode?.response_data?.data?.settings || [];
                const next = {};
                settings.forEach((setting) => {
                    if (setting?.monthly_frequency !== null && setting?.monthly_frequency !== undefined) {
                        next[setting.impact] = setting.monthly_frequency;
                    }
                });
                if (!cancelled) setFrequencyByImpact(next);
            } catch (error) {
                console.error('Failed to load assessment settings:', error);
                if (!cancelled) setMessage({ text: 'Could not load the current cadence.', tone: 'error' });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchSettings();
        return () => { cancelled = true; };
    }, [show]);

    // Vendors per tier, keyed by grade letter. Counted from the list already on
    // screen behind the modal, so this costs nothing.
    const gradeBuckets = useMemo(() => buildGradeBuckets(vendors), [vendors]);

    // A tier is "inverted" when it is left LONGER between scans than some
    // lower-risk tier above it — the running minimum interval is all that
    // needs comparing.
    const rows = useMemo(() => {
        let shortestSoFar = 0;
        return IMPACT_TIERS.map((tier) => {
            const months = Number(frequencyByImpact[tier.impact]) || 0;
            const inverted = months > 0 && shortestSoFar > 0 && months > shortestSoFar;
            if (months > 0 && (shortestSoFar === 0 || months < shortestSoFar)) shortestSoFar = months;
            const vendorCount = gradeBuckets[tier.short] || 0;
            return { ...tier, months, inverted, vendorCount, unset: months === 0 };
        });
    }, [frequencyByImpact, gradeBuckets]);

    const invertedCount = rows.filter((r) => r.inverted).length;
    // A tier with no cadence AND vendors in it is the actionable gap: those
    // vendors are never scheduled for reassessment.
    const uncoveredVendors = rows.reduce((sum, r) => sum + (r.unset ? r.vendorCount : 0), 0);

    const summary = useMemo(() => {
        const set = rows.filter((r) => !r.unset).map((r) => r.months);
        const shortest = set.length ? Math.min(...set) : null;
        const longest = set.length ? Math.max(...set) : null;
        return {
            graded: rows.reduce((sum, r) => sum + r.vendorCount, 0),
            tiersSet: set.length,
            // One concept, so one tile — two separate shortest/longest cards
            // were thin on their own and squeezed the labels onto two lines.
            range: shortest === null
                ? '—'
                : (shortest === longest ? `${shortest} mo` : `${shortest}–${longest} mo`),
        };
    }, [rows]);

    // `restore` puts the field back to the last saved value. The inputs are
    // uncontrolled (defaultValue + a key remount), so on a rejected edit there
    // is nothing to pull the typed text back — a `0` would sit there looking
    // saved. Writing to the node directly is the reliable way to revert an
    // uncontrolled input without making the whole grid controlled.
    const handleFrequencyBlur = async (impact, rawValue, restore) => {
        const stored = frequencyByImpact[impact];

        // Tabbed through, or cleared the field. Clearing is deliberately NOT
        // read as "unset this tier" — the endpoint takes a monthly_frequency
        // and has no documented null case — so put the stored value back
        // rather than leave a blank box that reads as cleared.
        if (rawValue === '') {
            restore(stored);
            return;
        }

        const value = parseInt(rawValue, 10);
        if (Number.isNaN(value) || value < 1) {
            restore(stored);
            setMessage({ text: 'Enter a whole number of months, 1 or more.', tone: 'error' });
            return;
        }

        // Unchanged — skip the round trip.
        if (value === stored) return;

        setSavingImpact(impact);
        setMessage(null);
        try {
            const response = await updateOrganizationAssessmentSettingsService({ impact, monthly_frequency: value });
            const ok = response?.statusCode?.response_data?.message || response?.statusCode?.message;
            if (ok) {
                setFrequencyByImpact((prev) => ({ ...prev, [impact]: value }));
                setSavedAny(true);
                setMessage({ text: ok, tone: 'success' });
            } else {
                const errMsg = response?.errors?.message || response?.error?.message
                    || response?.statusCode?.error?.message || 'Failed to update frequency.';
                restore(stored);
                setMessage({ text: errMsg, tone: 'error' });
            }
        } catch (error) {
            console.error('Failed to update assessment frequency:', error);
            restore(stored);
            setMessage({ text: 'Failed to update frequency.', tone: 'error' });
        } finally {
            setSavingImpact(null);
        }
    };

    // Refetch once, on the way out, and only if anything actually saved.
    const handleHide = () => {
        if (savedAny) onSaved?.();
        onHide?.();
    };

    return (
        <CommonModal
            show={show}
            onHide={handleHide}
            title="Scan Cadence"
            subtitle="Months between reassessments, by how likely a vendor is to be a problem. Riskier vendors should be scanned sooner. Each change saves as you leave the field."
            mid
        >
            {loading ? (
                <div className="tpcrm-loader"><ButtonLoader /></div>
            ) : (
                <>
                    <div className="tpcrm-cad-summary">
                        {[
                            {
                                icon: 'building',
                                label: 'Vendors',
                                value: summary.graded,
                                title: 'Vendors carrying a grade, so a cadence tier applies to them',
                            },
                            {
                                icon: 'audit',
                                label: 'Tiers set',
                                value: `${summary.tiersSet}/${IMPACT_TIERS.length}`,
                                title: 'Tiers with an interval configured',
                            },
                            {
                                icon: 'history',
                                label: 'Interval',
                                value: summary.range,
                                title: 'Shortest to longest configured interval',
                            },
                        ].map((stat) => (
                            <AddOnCard
                                key={stat.label}
                                icon={stat.icon}
                                label={stat.label}
                                title={stat.title}
                                style={{ height: '100%' }}
                                action={<span className="tpcrm-cad-stat-value">{stat.value}</span>}
                            />
                        ))}
                    </div>

                    {invertedCount > 0 && (
                        <div className="tpcrm-cad-warn" role="status">
                            <strong>Riskier vendors are being reassessed later than safer ones.</strong>
                            {' '}
                            {invertedCount === 1 ? 'One tier is' : `${invertedCount} tiers are`} marked below.
                            The interval normally shortens as risk rises.
                        </div>
                    )}

                    {uncoveredVendors > 0 && (
                        <div className="tpcrm-cad-warn" role="status">
                            <strong>
                                {uncoveredVendors === 1
                                    ? '1 vendor sits in a tier with no cadence set.'
                                    : `${uncoveredVendors} vendors sit in tiers with no cadence set.`}
                            </strong>
                            {' '}They will not be scheduled for reassessment until those tiers have a value.
                        </div>
                    )}

                    <div className="tpcrm-profile-table-wrap tpcrm-cad-table">
                        <CommonTable
                            headers={['Tier', 'Vendors', 'Interval', 'Reassessed']}
                            // No `rowStyle` background here: CommonTable puts an inline
                            // `backgroundColor: #FFFFFF` on every <td>, which paints over
                            // anything set on the <tr>, so row tinting silently does
                            // nothing. The two problem states get an explicit chip in the
                            // Tier cell instead — clearer than a tint would have been.
                            rows={rows.map((tier) => ({
                                data: [
                                    <span className="tpcrm-cad-tier">
                                        <span className="tpcrm-cad-badge" style={{ background: tier.color }}>{tier.short}</span>
                                        <span className="tpcrm-cad-label">{tier.label}</span>
                                        {tier.inverted && (
                                            <StatusBadge dot={false} variant="warning" size="sm">Later than safer tiers</StatusBadge>
                                        )}
                                        {tier.unset && tier.vendorCount > 0 && (
                                            <StatusBadge dot={false} variant="danger" size="sm">Not scheduled</StatusBadge>
                                        )}
                                    </span>,
                                    <span className={`tpcrm-cad-count${tier.vendorCount === 0 ? ' tpcrm-cad-count--zero' : ''}`}>
                                        {tier.vendorCount}
                                    </span>,
                                    <span className="tpcrm-cad-field">
                                        <input
                                            type="number"
                                            min="1"
                                            className="tpcrm-cadence-input tpcrm-cad-input"
                                            aria-label={`Months between scans for ${tier.label} vendors`}
                                            defaultValue={tier.months || ''}
                                            key={tier.months}
                                            disabled={savingImpact === tier.impact}
                                            onBlur={(e) => {
                                                const node = e.target;
                                                handleFrequencyBlur(tier.impact, node.value, (v) => { node.value = v ?? ''; });
                                            }}
                                        />
                                        {savingImpact === tier.impact && <ButtonLoader />}
                                    </span>,
                                    <span className="tpcrm-cad-interval">
                                        {tier.unset ? 'Not set' : intervalLabel(tier.months)}
                                    </span>,
                                ],
                            }))}
                            noPadding
                            noScroll
                        />
                    </div>

                    {message && (
                        <div className={`tpcrm-profile-msg${message.tone === 'error' ? ' tpcrm-profile-msg--error' : ''}`}>
                            {message.text}
                        </div>
                    )}
                </>
            )}
        </CommonModal>
    );
}

export default ScanCadenceModal;
